/* eslint-disable jira/naming/disallow-filenames */
// eslint-disable-next-line jira/filename-case
import React from 'react';
import type { ActionsType } from '@atlaskit/flag/types';
import { FlagsBoundary } from '@jira/platform__flags-boundary/src/FlagsBoundary.tsx';
import { lazyForPaint, lazyAfterPaint } from '@atlassian/react-loosely-lazy';
import type { JiraCommonFlag } from './common-flag-v2/index.tsx';
import type { FlagProps, AutoDismissFlagProps, CommonFlagProps } from './common-flag-v2/types.tsx';
import type { FlagGroupProps, FlagGroupType } from './group-flag-v2/index.tsx';

// eslint-disable-next-line jira/deprecations/no-rll-client-async-experiences
const ForPaintJiraCommonFlag = lazyForPaint<typeof JiraCommonFlag>(
	() =>
		import(/* webpackChunkName: "async-flags-v2" */ './common-flag-v2/index.tsx').then(
			({ JiraCommonFlag }) => JiraCommonFlag,
		),
	{ ssr: false },
);

// eslint-disable-next-line jira/deprecations/no-rll-client-async-experiences
const AfterPaintJiraCommonFlag = lazyAfterPaint<typeof JiraCommonFlag>(
	() =>
		// eslint-disable-next-line jira/import/webpack-magic-comment-entries
		import(/* webpackChunkName: "async-flags-v2" */ './common-flag-v2/index.tsx').then(
			({ JiraCommonFlag }) => JiraCommonFlag,
		),
	{ ssr: false },
);

const CommonFlagImpl = (props: CommonFlagProps) =>
	__SPA__ ? <AfterPaintJiraCommonFlag {...props} /> : <ForPaintJiraCommonFlag {...props} />;

// eslint-disable-next-line jira/deprecations/no-rll-client-async-experiences
const ForPaintFlagGroup = lazyForPaint<FlagGroupType>( // eslint-disable-next-line jira/import/webpack-magic-comment-entries
	() => import(/* webpackChunkName: "async-flags-v2" */ './group-flag-v2/index.tsx'),
	{ ssr: false },
);

// eslint-disable-next-line jira/deprecations/no-rll-client-async-experiences
const AfterPaintFlagGroup = lazyAfterPaint<FlagGroupType>( // eslint-disable-next-line jira/import/webpack-magic-comment-entries
	() => import(/* webpackChunkName: "async-flags-v2" */ './group-flag-v2/index.tsx'),
	{ ssr: false },
);

const FlagGroupImpl = (props: FlagGroupProps) =>
	__SPA__ ? <AfterPaintFlagGroup {...props} /> : <ForPaintFlagGroup {...props} />;

export type AsyncFlagProps = FlagProps & {
	packageName?: string;
};

export type AsyncAutoDismissFlagProps = AutoDismissFlagProps & {
	packageName?: string;
};

export type AsyncFlagGroupProps = FlagGroupProps & {
	packageName?: string;
};
export type CommonFlagAction = ActionsType[number];

export const CommonFlag = ({
	packageName = 'flags',
	...props
}: CommonFlagProps & {
	packageName?: string;
}): React.JSX.Element => (
	<FlagsBoundary packageName={packageName}>
		<CommonFlagImpl {...props} />
	</FlagsBoundary>
);

export const ErrorFlag = ({
	packageName = 'flags',
	...props
}: AsyncFlagProps): React.JSX.Element => (
	<FlagsBoundary packageName={packageName}>
		<CommonFlagImpl flagType="error" {...props} />
	</FlagsBoundary>
);

export const ErrorAutoDismissFlag = ({
	packageName = 'flags',
	...props
}: AsyncAutoDismissFlagProps): React.JSX.Element => (
	<FlagsBoundary packageName={packageName}>
		<CommonFlagImpl flagType="error" isAutoDismiss {...props} />
	</FlagsBoundary>
);

export const InfoFlag = ({
	packageName = 'flags',
	...props
}: AsyncFlagProps): React.JSX.Element => (
	<FlagsBoundary packageName={packageName}>
		<CommonFlagImpl flagType="info" {...props} />
	</FlagsBoundary>
);

export const SuccessAutoDismissFlag = ({
	packageName = 'flags',
	...props
}: AsyncAutoDismissFlagProps): React.JSX.Element => (
	<FlagsBoundary packageName={packageName}>
		<CommonFlagImpl flagType="success" isAutoDismiss {...props} />
	</FlagsBoundary>
);

export const SuccessFlag = ({
	packageName = 'flags',
	...props
}: AsyncFlagProps): React.JSX.Element => (
	<FlagsBoundary packageName={packageName}>
		<CommonFlagImpl flagType="success" {...props} />
	</FlagsBoundary>
);

export const WarningFlag = ({
	packageName = 'flags',
	...props
}: AsyncFlagProps): React.JSX.Element => (
	<FlagsBoundary packageName={packageName}>
		<CommonFlagImpl flagType="warning" {...props} />
	</FlagsBoundary>
);

export const FlagGroup = ({
	packageName = 'flags',
	...props
}: AsyncFlagGroupProps): React.JSX.Element => (
	<FlagsBoundary packageName={packageName}>
		<FlagGroupImpl {...props} />
	</FlagsBoundary>
);

// eslint-disable-next-line @atlassian/eng-health/no-barrel-files/disallow-reexports, jira/export/restrict-types-re-export
export type { FlagProps, AutoDismissFlagProps, CommonFlagProps };
